import { z } from 'zod';
/**
 * Copy-pasteable git for the weekly release train.
 *
 * Jay or Peter run these by hand (decision 4), so Atlas's job is to produce the
 * exact commands with the right SHAs interpolated — not to execute them. That
 * keeps a human at every point where history changes, which matters most for
 * the revert steps.
 *
 * The rules encoded here are not stylistic. Getting them wrong loses work:
 *
 *  - The production branch is cut from the FROZEN sha, never from a live head.
 *    Cutting from `uat` HEAD a week later sweeps in the next week's merges,
 *    shipping code nobody reviewed.
 *
 *  - A release branch merges to `main` ONLY. Merging it back into `develop` or
 *    `uat` carries the revert commits along and deletes the declined ticket's
 *    code from the line everyone works on.
 *
 *  - NOTHING is ever authored directly on a release branch except the decline
 *    reverts. A bug found in UAT is fixed on a branch cut from `develop`, merged
 *    to `develop` as normal, and cherry-picked onto the release.
 *
 *    This is what removes git-flow's back-merge entirely rather than replacing
 *    it: if the only commits on the branch are reverts that must NOT travel
 *    back, there is nothing to carry back, and the "did we remember to backport
 *    the release-branch fix" failure mode cannot happen. The branch is verified
 *    against that rule instead of reconciled after the fact.
 *
 *    Note the cherry-pick direction is load-bearing. MERGING a develop-based fix
 *    branch into the release would carry every other commit made to develop
 *    since the freeze — the exact unreviewed-code-rides-along problem the freeze
 *    exists to prevent.
 *
 *  - When a declined ticket returns in a later release, that release's branch
 *    must revert the PREVIOUS revert. `main` deleted those lines; the new branch
 *    never touched them, so a 3-way merge keeps main's side and the code
 *    silently never comes back — while Atlas reports the ticket as shipped.
 */
export const GitStepKind = z.enum(['freeze', 'cut', 'decline', 'carry_over', 'ship', 'resync', 'verify', 'fix']);
export const GitStep = z.object({
    kind: GitStepKind,
    title: z.string(),
    /** Why this step exists, in one line — shown under the title. */
    rationale: z.string(),
    commands: z.array(z.string()),
    /** Shown as a warning callout when present. */
    warning: z.string().nullable(),
});
export function hotfixBranchName(version) {
    return `hotfix/${version}`;
}
/**
 * Trailer stamped on every decline revert.
 *
 * Makes the commit self-describing so a backport job can skip it WITHOUT
 * calling Atlas — the alternative is an API lookup per commit, which fails
 * closed in exactly the situation you least want it to (Atlas down, release
 * day). It also makes `git log --grep` a complete history of every decline.
 */
export const DECLINE_TRAILER = 'Atlas-Decline';
const NEVER_BACK_MERGE = 'Never merge this branch into develop or uat — the revert commits would delete the declined work from the line everyone builds on.';
export function releaseBranchName(version) {
    return `release/${version}`;
}
/**
 * An urgent fix that bypasses the weekly train (decision 14).
 *
 * The shape is git-flow's hotfix branch — cut from production, merged back to
 * production — with one addition git-flow does not have to handle: this repo
 * keeps a long-lived `uat` branch, and a release may be sitting on it for a
 * week. A hotfix that reaches only `main` leaves BOTH other branches behind:
 * `develop` (so the next release is built against code that is not production)
 * and `uat` (so the business team reviews a base that no longer matches live).
 */
function buildHotfixSteps(ctx, b) {
    const branch = ctx.releaseBranch ?? hotfixBranchName(ctx.version);
    const steps = [
        {
            kind: 'cut',
            title: `Cut ${branch} from ${b.main}`,
            rationale: `A hotfix starts from what is LIVE, not from ${b.integration} — ${b.integration} contains next week's unreleased work, which must not ride along with an urgent fix.`,
            commands: [
                `git fetch origin`,
                `git checkout -b ${branch} origin/${b.main}`,
                `git push -u origin ${branch}`,
            ],
            warning: null,
        },
        {
            kind: 'ship',
            title: `Ship ${ctx.version} to ${b.main}`,
            rationale: 'Merge and tag as its own version so production history records the hotfix separately from the weekly release.',
            commands: [
                `git checkout ${b.main}`,
                `git pull`,
                `git merge --no-ff ${branch} -m "Hotfix ${ctx.version}"`,
                `git tag -a v${ctx.version} -m "Hotfix ${ctx.version}"`,
                `git push origin ${b.main} --follow-tags`,
            ],
            warning: null,
        },
        {
            kind: 'resync',
            title: `Carry the hotfix into ${b.integration}`,
            rationale: `Without this, ${b.integration} does not contain a fix that is live. Every branch cut from it afterwards is built against code that does not match production, and the next release can reintroduce the bug.`,
            commands: [
                `git log --oneline origin/${b.main} ^origin/${b.integration} --no-merges`,
                `git checkout ${b.integration} && git pull`,
                `git cherry-pick <hotfix-sha>`,
                `git push`,
            ],
            warning: `Cherry-pick, do not merge ${b.main} into ${b.integration} — a merge would also carry across the decline reverts that belong only on ${b.main}.`,
        },
    ];
    if (ctx.releaseInUat) {
        steps.push({
            kind: 'resync',
            title: `Carry the hotfix into ${b.uat} as well`,
            rationale: `${ctx.releaseInUat} is in UAT right now. Until this lands there, the business team is reviewing a base that no longer matches production and may re-report the bug you just fixed.`,
            commands: [
                `git checkout ${b.uat} && git pull`,
                `git cherry-pick <hotfix-sha>`,
                `git push`,
            ],
            warning: `Also cherry-pick it onto ${ctx.releaseInUat}'s release branch if that branch has already been cut, or the fix is missing from the very next production merge.`,
        });
    }
    steps.push({
        kind: 'ship',
        title: `Delete ${branch}`,
        rationale: 'Nothing should be able to merge it anywhere else later.',
        commands: [`git push origin --delete ${branch}`],
        warning: null,
    });
    return steps;
}
/** Build the ordered git steps for a release, given whatever is known so far. */
export function buildReleaseGitSteps(ctx) {
    const uat = ctx.uatBranch ?? 'uat';
    const source = ctx.sourceBranch ?? 'develop';
    const branch = ctx.releaseBranch ?? releaseBranchName(ctx.version);
    const main = ctx.productionBranch ?? 'main';
    const sha = ctx.commitSha ?? '<FROZEN_SHA>';
    const steps = [];
    if (ctx.isHotfix)
        return buildHotfixSteps(ctx, { main, uat, integration: ctx.integrationBranch ?? 'develop' });
    steps.push({
        kind: 'freeze',
        title: `Freeze ${ctx.version}`,
        rationale: `Record the ${uat} tip as the identity of this release. Everything after this is cut from that sha, not from a moving branch.`,
        commands: [`git fetch origin`, `git rev-parse origin/${uat}`],
        warning: ctx.commitSha ? null : 'Not frozen yet. Until a sha is recorded, the production branch has nothing safe to cut from.',
    });
    steps.push({
        kind: 'cut',
        title: `Cut ${branch}`,
        rationale: 'Branch from the frozen sha so a week of newer merges cannot ride along into production.',
        commands: [
            `git fetch origin`,
            `git checkout -b ${branch} ${sha}`,
            `git push -u origin ${branch}`,
        ],
        warning: NEVER_BACK_MERGE,
    });
    for (const d of ctx.declined) {
        steps.push({
            kind: 'decline',
            title: `Revert ${d.code} from ${branch}`,
            rationale: `${d.code} was declined in UAT. Its code is already merged, so it has to come back out of this branch only.`,
            commands: [
                `git checkout ${branch}`,
                `git log --oneline ${ctx.lastTag ?? '<LAST_PROD_TAG>'}..${sha} --grep=${d.code}`,
                `# squash-merge repo:   git revert --no-commit <sha>`,
                `# merge-commit repo:   git revert -m 1 --no-commit <merge-sha>`,
                // The trailer makes the commit self-describing: a backport job can skip
                // it without calling Atlas, and `git log --grep` finds every decline
                // ever made. Retrofitting this later would mean rewriting history.
                `git commit -m "Revert ${d.code} — declined in UAT for ${ctx.version}" -m "${DECLINE_TRAILER}: ${d.code}"`,
                `git push`,
            ],
            warning: 'Record the resulting revert sha against the ticket in Atlas — the release that carries this ticket next has to revert it, or the code never reaches main.',
        });
    }
    for (const c of ctx.carryOver) {
        steps.push({
            kind: 'carry_over',
            title: `Restore ${c.code} (declined in an earlier release)`,
            rationale: `main still carries the revert of ${c.code}. Without undoing it here, a 3-way merge keeps main's deletion and the code silently never ships.`,
            commands: [`git checkout ${branch}`, `git revert --no-edit ${c.priorRevertSha}`, `git push`],
            warning: null,
        });
    }
    steps.push(verifyStep({ branch, sha, source }));
    steps.push({
        kind: 'ship',
        title: `Ship ${ctx.version} to ${main}`,
        rationale: 'Merge, tag, push the tag, and delete the branch so its revert commits can never be merged anywhere else later.',
        commands: [
            `git checkout ${main}`,
            `git pull`,
            `git merge --no-ff ${branch} -m "Release ${ctx.version}"`,
            `git tag -a v${ctx.version} -m "Release ${ctx.version}"`,
            `git push origin ${main} --follow-tags`,
            `git push origin --delete ${branch}`,
        ],
        warning: `${source} keeps the declined work — that is deliberate. It re-qualifies for a later release on its own.`,
    });
    return steps;
}
/**
 * Pre-merge check that the branch obeys the no-authoring rule.
 *
 * Sits BEFORE the ship step, because it is a gate rather than a chore: anything
 * it prints is a commit that exists only here and would be lost from `develop`
 * the moment this branch is deleted. Under the rule it prints nothing, every
 * week, forever — which is the point.
 */
function verifyStep(args) {
    return {
        kind: 'verify',
        title: 'Check nothing was authored on the release branch',
        rationale: `Only decline reverts belong here. Anything else was written on the branch instead of on ${args.source}, and would vanish from ${args.source} when the branch is deleted.`,
        commands: [
            // --invert-grep hides the decline reverts, which are the one legitimate
            // exception. Whatever remains is a rule violation.
            `git log --oneline --invert-grep --grep="${DECLINE_TRAILER}:" ${args.sha}..${args.branch}`,
        ],
        warning: `Expect NO output. If anything is listed, cherry-pick it onto ${args.source} before deleting this branch — otherwise that work is lost. Then fix the process: it should have been a branch off ${args.source}.`,
    };
}
/**
 * Steps for a bug found during UAT (or anything else needing a change to a
 * release already cut).
 *
 * Deliberately never touches the release branch directly. The fix is ordinary
 * work on `develop`, and only the resulting commit is carried across — so the
 * ticket-link check, code review and the PR record all apply to it exactly as
 * they would to any other change.
 */
export function buildUatFixSteps(args) {
    const branch = args.releaseBranch ?? releaseBranchName(args.version);
    const source = args.sourceBranch ?? 'develop';
    const fixBranch = `fix/${args.ticketCode}`;
    return [
        {
            kind: 'fix',
            title: `Fix ${args.ticketCode} on a branch off ${source}`,
            rationale: `Never author on ${branch}. Cutting from ${source} keeps this an ordinary change — same review, same required checks, and ${source} ends up with the fix natively rather than needing it backported.`,
            commands: [
                `git fetch origin`,
                `git checkout -b ${fixBranch} origin/${source}`,
                `# …make the fix, commit, push…`,
                `git push -u origin ${fixBranch}`,
                `# open a PR into ${source} and merge it as normal`,
            ],
            warning: null,
        },
        {
            kind: 'fix',
            title: `Carry the merged fix onto ${branch}`,
            rationale: `Cherry-pick, never merge. Merging ${fixBranch} would bring every OTHER commit made to ${source} since the freeze along with it — the unreviewed work the freeze exists to keep out.`,
            commands: [
                `git log --oneline -1 origin/${source} --grep=${args.ticketCode}`,
                `git checkout ${branch} && git pull`,
                `git cherry-pick <sha-of-the-merged-fix>`,
                `git push`,
            ],
            warning: `Re-run the UAT deploy afterwards so the business team is reviewing the fixed build, and re-freeze if ${branch} has not been cut yet.`,
        },
    ];
}
//# sourceMappingURL=release-git.js.map